import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar, 
  CreditCard,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  Image as ImageIcon,
  Upload,
  Trash2,
  Send,
  Save,
  ExternalLink,
  FileText,
  Printer
} from 'lucide-react';
import { orderApi } from '@/services/orderApi';
import { courierApi } from '@/services/courierApi';
import { accountingApi } from '@/services/accountingApi';
import { productApi } from '@/services/productApi';
import { vendorApi } from '@/services/vendorApi';
import { Order } from '@/types/order';
import { CourierListItem } from '@/types/courier';
import { uploadImageToStorageV2, deleteImageFromStorage } from '@/utils/storage';
import { ProductDetailModal } from '@/components/ProductDetailModal';
import { Modal } from '@/components';
import { vendorOrderApi } from '@/services/vendorOrderApi';
import { vendorCourierApi } from '@/services/vendorCourierApi';
import { vendorProfileApi } from '@/services/vendorProfileApi';
import { useAuthStore } from '@/stores/authStore';
import toast from 'react-hot-toast';

const STATUS_LABELS: Record<string, string> = {
  awaiting_payment: 'Ödeme Bekleniyor',
  awaiting_bank_transfer: 'Havale Bekleniyor',
  payment_failed: 'Ödeme Başarısız',
  pending: 'Beklemede',
  confirmed: 'Onaylandı',
  preparing: 'Hazırlanıyor',
  on_the_way: 'Kurye Yolda',
  delivered: 'Teslim Edildi',
  cancelled: 'İptal',
};

const STATUS_COLORS: Record<string, string> = {
  awaiting_payment: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  awaiting_bank_transfer: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  payment_failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  confirmed: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  preparing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  on_the_way: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const parseOrderDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      return value.toDate();
    }
    const seconds = value._seconds ?? value.seconds;
    if (typeof seconds === 'number') {
      return new Date(seconds * 1000);
    }
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatOrderDate = (value: any): string | null => {
  const d = parseOrderDate(value);
  return d ? d.toLocaleString('tr-TR') : null;
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  'credit-card': 'Kredi Kartı',
  'bank-transfer': 'Havale / EFT',
  transfer: 'Havale',
  credit_card: 'Kredi Kartı',
  cash: 'Nakit',
};

const INVOICE_STATUS_LABELS: Record<string, string> = {
  none: 'Fatura Beklenmiyor',
  ready_to_invoice: 'Fatura Kesilmeye Hazır',
  processing: 'Kesiliyor',
  invoiced: 'Fatura Kesildi',
  failed: 'Hata Aldı',
  cancelled: 'İptal Edildi',
  refund_pending: 'İade Bekliyor',
};

const INVOICE_STATUS_COLORS: Record<string, string> = {
  none: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  ready_to_invoice: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  processing: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  invoiced: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  refund_pending: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
};

export const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isVendor = user?.role === 'vendor';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploadingPreview, setUploadingPreview] = useState(false);
  const [availableCouriers, setAvailableCouriers] = useState<CourierListItem[]>([]);
  const [selectedCourierId, setSelectedCourierId] = useState<string>('');
  const [courierFee, setCourierFee] = useState<string>('');
  const [assigningCourier, setAssigningCourier] = useState(false);
  const [vendors, setVendors] = useState<any[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [assigningVendor, setAssigningVendor] = useState(false);
  const [assignedCourierInfo, setAssignedCourierInfo] = useState<any>(null);
  const [loadingCourier, setLoadingCourier] = useState(false);
  const [assignedVendorInfo, setAssignedVendorInfo] = useState<any>(null);
  const [loadingVendor, setLoadingVendor] = useState(false);
  const [shipmentTrackingUrl, setShipmentTrackingUrl] = useState('');
  const [savingTrackingUrl, setSavingTrackingUrl] = useState(false);
  const [issuingInvoice, setIssuingInvoice] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [timeRemaining, setTimeRemaining] = useState<{ [key: string]: string }>({});

  // Sipariş iptali (nedenli)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Kredi kartı iadesi
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundType, setRefundType] = useState<'cancel' | 'refund'>('cancel');
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);

  // Kart Notu Yazdırma State
  const [showPrintCardModal, setShowPrintCardModal] = useState(false);
  const [cardPreset, setCardPreset] = useState<'10x15' | '8x12' | '80mm' | 'custom'>('10x15');
  const [cardWidth, setCardWidth] = useState<number>(100); // mm
  const [cardHeight, setCardHeight] = useState<number>(150); // mm
  const [topMargin, setTopMargin] = useState<number>(30); // mm
  const [leftMargin, setLeftMargin] = useState<number>(15); // mm
  const [fontSize, setFontSize] = useState<number>(16); // px
  const [fontFamily, setFontFamily] = useState<'cursive' | 'serif' | 'sans'>('cursive');
  const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right'>('center');
  const [includeRecipient, setIncludeRecipient] = useState(true);
  const [includeSender, setIncludeSender] = useState(true);
  const [includeOrderNo, setIncludeOrderNo] = useState(false);

  const handlePresetChange = (preset: '10x15' | '8x12' | '80mm' | 'custom') => {
    setCardPreset(preset);
    if (preset === '10x15') {
      setCardWidth(100); setCardHeight(150); setTopMargin(30); setLeftMargin(15); setFontSize(16);
    } else if (preset === '8x12') {
      setCardWidth(80); setCardHeight(120); setTopMargin(25); setLeftMargin(10); setFontSize(14);
    } else if (preset === '80mm') {
      setCardWidth(80); setCardHeight(100); setTopMargin(10); setLeftMargin(5); setFontSize(13);
    }
  };

  const handlePrintCard = () => {
    const printWindow = window.open('', '_blank', 'width=600,height=600');
    if (!printWindow) {
      toast.error('Baskı penceresi açılamadı. Lütfen tarayıcı pop-up engelleyicisini izin verin.');
      return;
    }

    const fontFamilyCss = 
      fontFamily === 'cursive' ? "'Dancing Script', 'Brush Script MT', cursive" :
      fontFamily === 'serif' ? "Georgia, 'Times New Roman', serif" :
      "system-ui, -apple-system, sans-serif";

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kart Notu - #${order?.orderNo || order?.id}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap" rel="stylesheet">
          <style>
            @page {
              size: ${cardWidth}mm ${cardHeight}mm;
              margin: 0;
            }
            html, body {
              margin: 0;
              padding: 0;
              width: ${cardWidth}mm;
              height: ${cardHeight}mm;
              box-sizing: border-box;
              font-family: ${fontFamilyCss};
              font-size: ${fontSize}px;
              text-align: ${textAlign};
              background: white;
              color: #000;
              overflow: hidden;
            }
            .card-container {
              padding-top: ${topMargin}mm;
              padding-left: ${leftMargin}mm;
              padding-right: ${leftMargin}mm;
              box-sizing: border-box;
              width: 100%;
            }
            .recipient {
              font-weight: bold;
              margin-bottom: 12px;
            }
            .message {
              white-space: pre-wrap;
              line-height: 1.5;
            }
            .sender {
              margin-top: 16px;
              font-weight: bold;
            }
            .footer-no {
              position: absolute;
              bottom: 4mm;
              right: 4mm;
              font-size: 9px;
              font-family: sans-serif;
              color: #888;
            }
          </style>
        </head>
        <body>
          <div class="card-container">
            ${includeRecipient && order?.recipientInfo?.fullName ? `<div class="recipient">Sevgili ${order.recipientInfo.fullName},</div>` : ''}
            <div class="message">${order?.cardNote || ''}</div>
            ${includeSender && order?.senderInfo?.name && !order?.anonymousSender ? `<div class="sender">— ${order.senderInfo.name}</div>` : ''}
          </div>
          ${includeOrderNo ? `<div class="footer-no">Sipariş #${order?.orderNo || order?.id}</div>` : ''}
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const loadOrder = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = isVendor
        ? await vendorOrderApi.getOrderById(id)
        : await orderApi.getOrderById(id);
      setOrder(response);
      setShipmentTrackingUrl(response.shipmentTrackingUrl || '');
    } catch (error) {
      console.error('Sipariş yüklenemedi:', error);
      toast.error('Sipariş yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
    if (isAdmin) {
      loadVendors();
    }
  }, [id, isAdmin, isVendor]);

  const loadVendors = async () => {
    try {
      const response = await vendorApi.getAllVendors({ status: 'active', limit: 100 });
      setVendors(response.vendors || []);
    } catch (error) {
      console.error('Vendor listesi yüklenemedi:', error);
      setVendors([]);
    }
  };

  const loadAvailableCouriers = async (vendorId: string) => {
    try {
      if (isVendor) {
        const response = await vendorCourierApi.getCouriers({ status: 'active', limit: 100, offset: 0 });
        setAvailableCouriers(response.couriers || []);
      } else {
        const response = await courierApi.getVendorCouriers(vendorId);
        setAvailableCouriers(response.couriers || []);
      }
      setSelectedCourierId('');
      setCourierFee('');
    } catch (error) {
      console.error('Kuryeler yüklenemedi:', error);
      setAvailableCouriers([]);
    }
  };

  // Order yüklendiğinde veya vendor değiştiğinde kuryeler yükle
  useEffect(() => {
    if (order?.assignedVendorId) {
      loadAvailableCouriers(order.assignedVendorId);
      // Vendor'ın default fee'sini göster
      // (Bu bilgi vendor detayından çekilebilir, şimdilik form boş başlar)
    }
  }, [order?.assignedVendorId]);

  // Assigned courier info'yu yükle
  useEffect(() => {
    if (order?.assignedCourierId) {
      const fetchCourierInfo = async () => {
        setLoadingCourier(true);
        try {
          const courierId = order.assignedCourierId;
          if (courierId) {
            const courier = isVendor
              ? await vendorCourierApi.getCourier(courierId)
              : await courierApi.getCourier(courierId);
            setAssignedCourierInfo(courier);
          }
        } catch (error) {
          console.error('Kurye bilgisi yüklenemedi:', error);
          setAssignedCourierInfo(null);
        } finally {
          setLoadingCourier(false);
        }
      };
      fetchCourierInfo();
    } else {
      setAssignedCourierInfo(null);
    }
  }, [order?.assignedCourierId, isVendor]);

  // Assigned vendor info'yu yükle
  useEffect(() => {
    if (order?.assignedVendorId) {
      const fetchVendorInfo = async () => {
        setLoadingVendor(true);
        try {
          const vendorId = order.assignedVendorId;
          if (vendorId) {
            const vendor = isVendor
              ? await vendorProfileApi.getProfile()
              : await vendorApi.getVendor(vendorId);
            setAssignedVendorInfo(vendor);
          }
        } catch (error) {
          console.error('Satıcı bilgisi yüklenemedi:', error);
          setAssignedVendorInfo(null);
        } finally {
          setLoadingVendor(false);
        }
      };
      fetchVendorInfo();
    } else {
      setAssignedVendorInfo(null);
    }
  }, [order?.assignedVendorId, isVendor]);

  // Countdown timer
  useEffect(() => {
    // İlk hesaplama
    const calculateTimeRemaining = () => {
      const remaining: { [key: string]: string } = {};
      if (order?.items) {
        order.items.forEach((item: any, idx: number) => {
          // item.deliveryTime bir aralık string'idir (ör: "15:00-18:00"). Doğrudan Date()'e verilirse
          // "-18:00" saat dilimi offset'i sanılıp tarih hesaplaması bozuluyordu — pencerenin BİTİŞ
          // saatini (aralığın sağ tarafını) hedef alıyoruz.
          const timeRange = String(item.deliveryTime || '');
          const endTime = timeRange.includes('-') ? timeRange.split('-')[1]?.trim() : timeRange.trim();
          const deliveryDateTime = new Date(`${item.deliveryDate}T${endTime}`);

          if (!endTime || Number.isNaN(deliveryDateTime.getTime())) {
            remaining[idx] = '';
            return;
          }

          const now = new Date();
          const diff = deliveryDateTime.getTime() - now.getTime();

          if (diff <= 0) {
            remaining[idx] = 'Teslimat süresi geçti';
          } else {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            
            if (days > 0) {
              remaining[idx] = `${days}g ${hours}s kaldı`;
            } else if (hours > 0) {
              remaining[idx] = `${hours}s ${minutes}d kaldı`;
            } else {
              remaining[idx] = `${minutes}d kaldı`;
            }
          }
        });
      }
      setTimeRemaining(remaining);
    };

    // İlk hesapla
    calculateTimeRemaining();

    // Her 60 saniyede bir güncelle
    const interval = setInterval(calculateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [order]);

  const handleStatusChange = async (newStatus: string) => {
    if (!id || !order) return;

    // İptal, neden zorunlu olduğu için ayrı bir modal ile yönetiliyor
    if (newStatus === 'cancelled' && !isVendor) {
      setCancelReason('');
      setShowCancelModal(true);
      return;
    }

    setUpdating(true);
    try {
      const updatedOrder = isVendor
        ? await vendorOrderApi.updateOrderStatus(id, newStatus)
        : await orderApi.updateOrderStatus(id, newStatus).then(() => ({ ...order, status: newStatus as any } as Order));
      setOrder(updatedOrder);
      toast.success('Sipariş durumu güncellendi');
    } catch (error: any) {
      console.error('Durum güncellenemedi:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Durum güncellenemedi';
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!id || !order) return;
    if (!cancelReason.trim()) {
      toast.error('Lütfen iptal nedenini yazın');
      return;
    }

    setCancelling(true);
    try {
      const updatedOrder = await orderApi.cancelOrder(id, cancelReason.trim());
      setOrder(updatedOrder);
      toast.success('Sipariş iptal edildi');
      setShowCancelModal(false);
    } catch (error: any) {
      toast.error(error.message || 'Sipariş iptal edilemedi');
    } finally {
      setCancelling(false);
    }
  };

  const openRefundModal = () => {
    if (!order) return;
    setRefundAmount(String(order.totalPrice));
    setRefundType('cancel');
    setRefundReason('');
    setShowRefundModal(true);
  };

  const handleConfirmRefund = async () => {
    if (!id) return;
    const amountNum = Number(refundAmount);
    if (!amountNum || amountNum <= 0) {
      toast.error('Geçerli bir iade tutarı girin');
      return;
    }
    if (!refundReason.trim()) {
      toast.error('Lütfen iade nedenini yazın');
      return;
    }

    setRefunding(true);
    try {
      await orderApi.refundOrder(id, { amount: amountNum, type: refundType, reason: refundReason.trim() });
      toast.success(refundType === 'cancel' ? 'Ödeme iptal edildi' : 'Ödeme iade edildi');
      setShowRefundModal(false);
      await loadOrder();
    } catch (error: any) {
      toast.error(error.message || 'İade/iptal işlemi başarısız');
    } finally {
      setRefunding(false);
    }
  };

  const handlePaymentProcessed = async () => {
    if (!id || !order) return;

    setUpdating(true);
    try {
      const updatedOrder = isVendor
        ? await vendorOrderApi.markPaymentProcessed(id)
        : await orderApi.markPaymentProcessed(id).then(() => ({
          ...order,
          paymentInfo: {
            ...order.paymentInfo,
            isProcessed: true,
            processedAt: new Date(),
          },
        } as Order));
      setOrder(updatedOrder);
      toast.success('Ödeme işlendi olarak işaretlendi');
    } catch (error: any) {
      console.error('Ödeme durumu güncellenemedi:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Ödeme durumu güncellenemedi');
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmBankTransfer = async () => {
    if (!id || !order) return;
    if (!window.confirm('Havale ödemesi alındığını onaylıyor musunuz? Sipariş "Onaylandı" durumuna geçecek.')) return;

    setUpdating(true);
    try {
      await orderApi.confirmBankTransfer(id);
      setOrder({
        ...order,
        status: 'confirmed',
        paymentInfo: {
          ...order.paymentInfo,
          isProcessed: true,
          processedAt: new Date(),
          bankTransferConfirmedAt: new Date() as any,
        },
      } as Order);
      toast.success('Havale onaylandı');
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || 'Havale onaylanamadı');
    } finally {
      setUpdating(false);
    }
  };

  const handlePreviewUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!id || !order || !event.target.files || event.target.files.length === 0) return;

    const file = event.target.files[0];
    
    // Dosya boyutu kontrolü (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu en fazla 5MB olabilir');
      return;
    }

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      toast.error('Sadece resim dosyaları yüklenebilir');
      return;
    }

    setUploadingPreview(true);
    try {
      // Firebase Storage'a yükle
      const downloadURL = await uploadImageToStorageV2(file, `orders/${id}/preview`, 'order-preview');
      
      // Backend'e URL'i kaydet
      if (isVendor) {
        await vendorOrderApi.uploadOrderPreview(id, downloadURL);
      } else {
        await orderApi.uploadOrderPreview(id, downloadURL);
      }
      
      await loadOrder(); // Sayfayı yenile
      toast.success('Sipariş önizleme görseli yüklendi');
    } catch (error: any) {
      console.error('Görsel yüklenemedi:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Görsel yüklenemedi');
    } finally {
      setUploadingPreview(false);
      // Input'u temizle
      event.target.value = '';
    }
  };

  const handleDeletePreview = async () => {
    if (!id || !order || !order.orderPreview?.photoUrl) return;

    if (!window.confirm('Sipariş önizleme görselini silmek istediğinize emin misiniz?')) {
      return;
    }

    setUpdating(true);
    try {
      // Storage'dan sil (dosya zaten silinmiş olabilir, hata alınsa bile devam et)
      try {
        await deleteImageFromStorage(order.orderPreview.photoUrl);
      } catch (storageError) {
        console.warn('Storage silme hatası (dosya zaten silinmiş olabilir):', storageError);
      }
      
      // Backend'de orderPreview'i her zaman temizle
      if (isVendor) {
        await vendorOrderApi.uploadOrderPreview(id, '');
      } else {
        await orderApi.uploadOrderPreview(id, '');
      }
      
      // Sunucudan güncel veriyi çek (UI'ı garantili şekilde günceller)
      await loadOrder();
      
      toast.success('Sipariş önizleme görseli silindi');
    } catch (error: any) {
      console.error('Görsel silinemedi:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Görsel silinemedi');
      // Hata olsa bile UI'ı güncelle
      await loadOrder();
    } finally {
      setUpdating(false);
    }
  };

  const handleAssignVendor = async () => {
    if (!id || !selectedVendorId) {
      toast.error('Lütfen satıcı seçiniz');
      return;
    }

    setAssigningVendor(true);
    try {
      await orderApi.assignVendor(id, selectedVendorId);
      
      // Order'ı yeniden yükle
      await loadOrder();
      
      toast.success('Satıcı atandı');
      setSelectedVendorId('');
    } catch (error: any) {
      console.error('Satıcı atama hatası:', error);
      toast.error(error.message || 'Satıcı ataması başarısız');
    } finally {
      setAssigningVendor(false);
    }
  };

  const handleAssignCourier = async () => {
    if (!id || !order || !selectedCourierId) {
      toast.error('Lütfen kurye seçiniz');
      return;
    }

    setAssigningCourier(true);
    try {
      const fee = courierFee ? parseFloat(courierFee) : undefined;
      if (isVendor) {
        await vendorOrderApi.assignCourierWithFee(id, selectedCourierId, fee);
      } else {
        await accountingApi.assignCourierToOrder(id, selectedCourierId, fee);
      }
      
      // Order'ı yeniden yükle
      await loadOrder();
      
      // Form'u temizle
      setSelectedCourierId('');
      setCourierFee('');
      
      toast.success('Kurye atandı');
    } catch (error: any) {
      console.error('Kurye atama hatası:', error);
      toast.error(error.message || 'Kurye ataması başarısız');
    } finally {
      setAssigningCourier(false);
    }
  };

  const handleShipmentTrackingSave = async () => {
    if (!id || !order || !isAdmin) return;

    setSavingTrackingUrl(true);
    try {
      const updatedOrder = await orderApi.updateShipmentTrackingUrl(id, shipmentTrackingUrl);
      setOrder(updatedOrder);
      setShipmentTrackingUrl(updatedOrder.shipmentTrackingUrl || '');
      toast.success(shipmentTrackingUrl.trim() ? 'Takip linki kaydedildi' : 'Takip linki temizlendi');
    } catch (error: any) {
      console.error('Takip linki güncellenemedi:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Takip linki güncellenemedi');
    } finally {
      setSavingTrackingUrl(false);
    }
  };

  const handleIssueInvoice = async () => {
    if (!id || !order || !isAdmin) return;

    setIssuingInvoice(true);
    try {
      const updatedOrder = await orderApi.issueInvoice(id);
      setOrder(updatedOrder);
      toast.success('Fatura başarıyla kesildi');
    } catch (error: any) {
      console.error('Fatura kesilemedi:', error);
      toast.error(error.message || 'Fatura kesilemedi');
    } finally {
      setIssuingInvoice(false);
    }
  };

  const formatCurrency = (value: number) => `₺${value.toFixed(2)}`;

  const getInvoicePreviewRows = () => {
    if (!order) {
      return [] as Array<{ name: string; quantity: number; unitPrice: number; total: number }>;
    }

    const itemRows = order.items.map((item) => {
      const unitPrice = item.campaignPrice > 0 ? item.campaignPrice : item.basePrice;
      return {
        name: item.title,
        quantity: item.quantity,
        unitPrice,
        total: unitPrice * item.quantity,
      };
    });

    const extraRows = (order.extraProducts || []).map((item) => ({
      name: item.name,
      quantity: 1,
      unitPrice: item.price,
      total: item.price,
    }));

    const deliveryRow = order.totalDeliveryFee > 0
      ? [{
          name: 'Teslimat Ücreti',
          quantity: 1,
          unitPrice: order.totalDeliveryFee,
          total: order.totalDeliveryFee,
        }]
      : [];

    return [...itemRows, ...extraRows, ...deliveryRow];
  };

  const invoicePreviewRows = getInvoicePreviewRows();
  const invoicePreviewSubTotal = invoicePreviewRows.reduce((sum, row) => sum + row.total, 0);
  const invoicePreviewDiscount = order?.coupon?.discountAmount || 0;
  const invoicePreviewTaxBase = invoicePreviewSubTotal - invoicePreviewDiscount;
  const invoicePreviewNet = Number((invoicePreviewTaxBase / 1.2).toFixed(2));
  const invoicePreviewTax = Number((invoicePreviewTaxBase - invoicePreviewNet).toFixed(2));

  const copyToClipboard = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopyalandı`);
  };

  const getWhatsAppUrl = (phone: string) => {
    if (!phone) return '#';
    const cleanPhone = phone.replace(/\D/g, '');
    const formatted = cleanPhone.startsWith('90') ? cleanPhone : `90${cleanPhone}`;
    return `https://wa.me/${formatted}`;
  };

  const STEPPER_STAGES = [
    { key: 'pending', label: 'Beklemede', icon: Clock },
    { key: 'confirmed', label: 'Onaylandı', icon: CheckCircle },
    { key: 'preparing', label: 'Hazırlanıyor', icon: Package },
    { key: 'on_the_way', label: 'Kurye Yolda', icon: Truck },
    { key: 'delivered', label: 'Teslim Edildi', icon: CheckCircle },
  ];

  const getStageIndex = (status: string) => {
    switch (status) {
      case 'awaiting_payment':
      case 'awaiting_bank_transfer':
      case 'pending': return 0;
      case 'confirmed': return 1;
      case 'preparing': return 2;
      case 'on_the_way': return 3;
      case 'delivered': return 4;
      default: return -1;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Sipariş detayları yükleniyor...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center">
        <XCircle className="w-12 h-12 text-rose-500" />
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Sipariş Bulunamadı</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">İstediğiniz sipariş silinmiş veya mevcut olmayabilir.</p>
        </div>
        <button
          onClick={() => navigate(isVendor ? '/vendor/orders' : '/admin/orders')}
          className="px-5 py-2.5 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-xl font-medium text-sm hover:bg-slate-800 transition"
        >
          Sipariş Listesine Dön
        </button>
      </div>
    );
  }

  const currentStageIdx = getStageIndex(order.status);
  const isCancelled = order.status === 'cancelled' || order.status === 'payment_failed';

  return (
    <div className="p-4 md:p-6 lg:p-8 w-full space-y-6 transition-colors">
      {/* Product Detail Modal */}
      <ProductDetailModal
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        allowCostEdit={!!order && order.status !== 'delivered' && order.status !== 'cancelled'}
      />

      {/* Top Header & Actions Bar */}
      <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            onClick={() => navigate(isVendor ? '/vendor/orders' : '/admin/orders')}
            className="p-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl transition active:scale-95"
            title="Sipariş Listesine Dön"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                {order.orderNo ? `Sipariş #${order.orderNo}` : 'Sipariş Detayı'}
              </h1>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[order.status] || 'bg-slate-100 text-slate-800'}`}>
                {STATUS_LABELS[order.status] || order.status}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mt-1 flex-wrap">
              <span className="font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition" onClick={() => copyToClipboard(order.id, 'Sipariş ID')}>
                ID: {order.id} 📋
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(order.createdAt).toLocaleString('tr-TR')}
              </span>
            </div>
          </div>
        </div>

        {/* Header Action Buttons */}
        <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
          {order.invoiceInfo && isAdmin && (
            <button
              onClick={() => setShowInvoicePreview(true)}
              className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition flex items-center gap-1.5"
            >
              <FileText className="w-4 h-4" />
              Fatura Önizle
            </button>
          )}
          {order.status !== 'cancelled' && order.status !== 'delivered' && !isVendor && (
            <button
              onClick={() => { setCancelReason(''); setShowCancelModal(true); }}
              className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/50 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800/60 rounded-xl text-xs font-semibold transition"
            >
              Siparişi İptal Et
            </button>
          )}
        </div>
      </div>

      {/* Operational Stepper Timeline */}
      <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-4">
          Sipariş İlerleme Durumu
        </h3>

        {isCancelled ? (
          <div className="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 rounded-xl text-rose-800 dark:text-rose-300 text-sm font-medium flex items-center gap-3">
            <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0" />
            <div>
              <p className="font-bold">Bu sipariş {STATUS_LABELS[order.status]} durumundadır.</p>
              {(order as any).cancelReason && <p className="text-xs mt-0.5 text-rose-700 dark:text-rose-400">Nedeni: {(order as any).cancelReason}</p>}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 relative">
            {STEPPER_STAGES.map((stg, idx) => {
              const isPast = idx < currentStageIdx;
              const isCurrent = idx === currentStageIdx;
              const IconComp = stg.icon;

              const getStageTimestamp = (stgKey: string) => {
                if (order.statusTimestamps?.[stgKey]) return order.statusTimestamps[stgKey];
                if (stgKey === 'pending') return order.createdAt || null;
                if (stgKey === 'confirmed') return (order as any).confirmedAt || null;
                if (stgKey === 'preparing') return (order as any).preparingAt || null;
                if (stgKey === 'on_the_way') return order.deliveryStartedAt || order.courierAcceptedAt || null;
                if (stgKey === 'delivered') return order.deliveryProof?.completedAt || (order as any).deliveredAt || null;
                return null;
              };

              const rawTime = getStageTimestamp(stg.key);
              const dateObj = parseOrderDate(rawTime);
              const formattedTime = dateObj
                ? dateObj.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                : null;

              return (
                <button
                  key={stg.key}
                  onClick={() => handleStatusChange(stg.key)}
                  disabled={updating}
                  className={`p-3 rounded-xl border transition flex flex-col items-center justify-center text-center gap-1 relative ${
                    isCurrent
                      ? 'bg-emerald-500 text-white border-emerald-600 shadow-sm font-bold scale-[1.02]'
                      : isPast
                      ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60 text-emerald-800 dark:text-emerald-300'
                      : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-700'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${isCurrent ? 'bg-white/20' : isPast ? 'bg-emerald-100 dark:bg-emerald-900/60 text-emerald-600 dark:text-emerald-400' : 'bg-slate-200 dark:bg-slate-700'}`}>
                    <IconComp className="w-4 h-4" />
                  </div>
                  <span className="text-xs">{stg.label}</span>
                  {formattedTime && (
                    <span className={`text-[10px] font-normal mt-0.5 ${isCurrent ? 'text-emerald-100' : 'text-slate-500 dark:text-slate-400'}`}>
                      {formattedTime}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Urgent Delivery & Countdown Banner */}
      {order.items && order.items.length > 0 && (
        <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent dark:from-amber-500/20 dark:via-amber-500/10 dark:to-slate-900 border border-amber-300/60 dark:border-amber-700/60 rounded-2xl p-5 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 bg-amber-500/20 dark:bg-amber-500/30 text-amber-700 dark:text-amber-300 rounded-xl flex items-center justify-center shrink-0">
              <Clock className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-400">
                Teslimat Zamanı & Durumu
              </p>
              <h4 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
                {order.items[0]?.isShippable ? (
                  <span>📦 Kargo Teslimatlı Ürün — {order.items[0]?.estimatedShippingDays ? `${order.items[0].estimatedShippingDays} gün içinde kargo çıkışı` : 'Kargo süreci bekleniyor'}</span>
                ) : (
                  <span>
                    📅 {new Date(order.items[0]?.deliveryDate).toLocaleDateString('tr-TR')} — Saat Aralığı: <span className="underline">{order.items[0]?.deliveryTime}</span>
                  </span>
                )}
              </h4>
            </div>
          </div>

          {!order.items[0]?.isShippable && order.status !== 'delivered' && order.status !== 'cancelled' && (
            <div className="flex items-center gap-2 self-start md:self-auto bg-amber-100 dark:bg-amber-950/80 border border-amber-300 dark:border-amber-800 px-4 py-2 rounded-xl text-amber-900 dark:text-amber-200 font-bold text-sm">
              <span>⏱️ Kalan Süre:</span>
              <span className={timeRemaining[0]?.includes('geçti') ? 'text-rose-600 dark:text-rose-400 font-black' : 'text-amber-800 dark:text-amber-300'}>
                {timeRemaining[0] || 'Hesaplanıyor...'}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Kupon Kullanımı Banner */}
      {order.coupon && (
        <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 rounded-2xl p-5 shadow-xs flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded-xl flex items-center justify-center text-xl font-bold">
              🏷️
            </div>
            <div>
              <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Kullanılan Kupon Kodu</p>
              <p className="text-lg font-bold text-emerald-950 dark:text-emerald-200">{order.coupon.code}</p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                {order.coupon.type === 'percentage' ? `%${order.coupon.discountValue} oranında indirim` : `₺${order.coupon.discountValue} tutarında sabit indirim`}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Toplam İndirim</p>
            <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">-₺{order.coupon.discountAmount.toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Main Grid: Left Column (7 cols) & Right Column (5 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column (7 cols) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Order Products Card */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs p-5 space-y-4">
            <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              Sipariş Edilen Ürünler
            </h2>

            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedProduct({ ...item, index })}
                  className="flex flex-col sm:flex-row sm:items-start gap-4 p-4 bg-slate-50 dark:bg-slate-800/40 rounded-xl cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition border border-slate-200/60 dark:border-slate-800 group"
                >
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl border border-slate-200 dark:border-slate-700 shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-400 shrink-0">
                      Görsel Yok
                    </div>
                  )}

                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-slate-900 dark:text-slate-100 text-base group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition">
                        {item.title}
                      </h3>
                      <span className="px-2.5 py-1 bg-slate-200 dark:bg-slate-700 rounded-full text-xs font-bold text-slate-700 dark:text-slate-300">
                        {item.quantity} adet
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      {item.campaignPrice < item.basePrice && (
                        <span className="text-xs text-slate-400 line-through font-mono">
                          ₺{item.basePrice.toFixed(2)}
                        </span>
                      )}
                      <span className="font-bold text-emerald-600 dark:text-emerald-400 text-lg font-mono">
                        ₺{(item.campaignPrice > 0 ? item.campaignPrice : item.basePrice).toFixed(2)}
                      </span>
                    </div>

                    {/* Inline Product Cost Editor (Admin only) */}
                    {isAdmin && item.id && (
                      <ProductCostInlineEditor productId={item.id} />
                    )}

                    {/* Item Extra Products */}
                    {item.extraProducts && item.extraProducts.length > 0 && (
                      <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl border border-emerald-100 dark:border-emerald-800/40 space-y-1">
                        <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                          🎁 Ekstra Hediye Ürünler
                        </p>
                        {item.extraProducts.map((extra: any, extraIdx: number) => (
                          <div key={extraIdx} className="flex justify-between items-center text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                            <span>{extra.name || extra.title || 'Ekstra Ürün'}</span>
                            <span className="font-mono font-bold">+₺{Number(extra.price).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Financial Summary */}
            <div className="pt-4 border-t border-slate-100 dark:border-slate-800 space-y-2 text-sm">
              {order.itemsTotal !== undefined && (
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Ürünler Toplamı</span>
                  <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">₺{order.itemsTotal.toFixed(2)}</span>
                </div>
              )}
              {order.totalDeliveryFee !== undefined && order.totalDeliveryFee > 0 && (
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Teslimat Ücreti</span>
                  <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">₺{order.totalDeliveryFee.toFixed(2)}</span>
                </div>
              )}
              {order.extraProductsTotal !== undefined && order.extraProductsTotal > 0 && (
                <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
                  <span>Ekstra Ürünler Toplamı</span>
                  <span className="font-mono font-semibold">+₺{order.extraProductsTotal.toFixed(2)}</span>
                </div>
              )}
              {order.coupon && (
                <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-semibold">
                  <span>Kupon İndirimi ({order.coupon.code})</span>
                  <span className="font-mono">-₺{order.coupon.discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-3 border-t border-slate-200 dark:border-slate-800 text-base font-bold">
                <span className="text-slate-900 dark:text-white">Genel Toplam Tutar</span>
                <span className="text-xl text-emerald-600 dark:text-emerald-400 font-mono">
                  ₺{order.totalPrice.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Cards: Alıcı vs Gönderen */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Alıcı Bilgileri (Teslim Alacak Kişi) - Rose/Pink Theme */}
            <div className="bg-rose-50/50 dark:bg-rose-950/20 rounded-2xl border border-rose-200 dark:border-rose-900/40 p-5 space-y-3.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400 font-bold text-sm">
                  <User className="w-4 h-4" />
                  Alıcı Bilgileri
                </div>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300">
                  Teslim Alacak Kişi
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Ad Soyad</p>
                  <p className="font-bold text-slate-900 dark:text-slate-100 text-sm">{order.recipientInfo.fullName}</p>
                </div>

                <div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Telefon</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-semibold text-slate-800 dark:text-slate-200 font-mono">{order.recipientInfo.phone}</span>
                    <a
                      href={`tel:${order.recipientInfo.phone}`}
                      className="p-1 rounded bg-rose-100 dark:bg-rose-900 text-rose-700 dark:text-rose-300 hover:scale-105 transition"
                      title="Ara"
                    >
                      <Phone className="w-3.5 h-3.5" />
                    </a>
                    <a
                      href={getWhatsAppUrl(order.recipientInfo.phone)}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 hover:scale-105 transition"
                      title="WhatsApp'ta Aç"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-slate-500 dark:text-slate-400 text-[11px]">Teslimat Adresi</p>
                    <button
                      onClick={() => copyToClipboard(`${order.recipientInfo.address || ''} ${order.addressDetails || ''}`, 'Adres')}
                      className="text-[10px] font-semibold text-rose-600 dark:text-rose-400 hover:underline"
                    >
                      Kopyala 📋
                    </button>
                  </div>
                  <p className="font-medium text-slate-800 dark:text-slate-200 leading-snug mt-0.5">
                    {order.recipientInfo.address || 'Adres belirtilmemiş'}
                  </p>
                  {order.addressDetails && (
                    <p className="text-slate-500 dark:text-slate-400 text-[11px] mt-1 italic">
                      Detay: {order.addressDetails}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Gönderen Bilgileri (Siparişi Veren Müşteri) - Indigo Theme */}
            <div className="bg-indigo-50/50 dark:bg-indigo-950/20 rounded-2xl border border-indigo-200 dark:border-indigo-900/40 p-5 space-y-3.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-400 font-bold text-sm">
                  <User className="w-4 h-4" />
                  Gönderen Bilgileri
                </div>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300">
                  Siparişi Veren
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Ad Soyad</p>
                  <p className="font-bold text-slate-900 dark:text-slate-100 text-sm">{order.senderInfo.name}</p>
                </div>

                <div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Telefon</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-semibold text-slate-800 dark:text-slate-200 font-mono">{order.senderInfo.phone}</span>
                    <a
                      href={`tel:${order.senderInfo.phone}`}
                      className="p-1 rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 hover:scale-105 transition"
                      title="Ara"
                    >
                      <Phone className="w-3.5 h-3.5" />
                    </a>
                    <a
                      href={getWhatsAppUrl(order.senderInfo.phone)}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 hover:scale-105 transition"
                      title="WhatsApp'ta Aç"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>

                <div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">E-posta</p>
                  <p className="font-medium text-slate-800 dark:text-slate-200 truncate mt-0.5">{order.senderInfo.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Müşteri Notları & Kart Mesajı Card */}
          {(order.cardNote || order.orderNote) && (
            <div className="bg-purple-50/60 dark:bg-purple-950/20 rounded-2xl border border-purple-200 dark:border-purple-900/40 p-5 space-y-4 shadow-xs">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-purple-900 dark:text-purple-200 flex items-center gap-2">
                  <span>💌</span>
                  Müşteri Kart Notı & Sipariş Notu
                </h2>
                {order.anonymousSender && (
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-200 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                    🕵️ Anonim Gönderici
                  </span>
                )}
              </div>

              <div className="space-y-3">
                {order.cardNote && (
                  <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-purple-200 dark:border-purple-800 shadow-xs relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wider">
                        Çiçek/Hediye Kart Mesajı
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowPrintCardModal(true)}
                          className="text-xs text-purple-700 dark:text-purple-300 font-semibold bg-purple-100 dark:bg-purple-900/60 px-2.5 py-1 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-800 transition flex items-center gap-1"
                        >
                          <Printer className="w-3.5 h-3.5" />
                          Kartı Yazdır
                        </button>
                        <button
                          onClick={() => copyToClipboard(order.cardNote || '', 'Kart mesajı')}
                          className="text-xs text-purple-600 dark:text-purple-400 font-semibold hover:underline"
                        >
                          Kopyala 📋
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-900 dark:text-slate-100 italic whitespace-pre-wrap leading-relaxed font-serif">
                      "{order.cardNote}"
                    </p>
                  </div>
                )}

                {order.orderNote && (
                  <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-purple-200 dark:border-purple-800 shadow-xs">
                    <span className="text-xs font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wider block mb-1">
                      Özel Sipariş Notu
                    </span>
                    <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">
                      {order.orderNote}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Invoice Info Details */}
          {order.invoiceInfo && (
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 space-y-3 shadow-xs">
              <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                Fatura Bilgileri
              </h2>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-slate-400">Fatura Tipi</p>
                  <p className="font-semibold text-slate-800 dark:text-slate-200">
                    {order.invoiceInfo.type === 'individual' ? 'Bireysel Fatura' : 'Kurumsal Fatura'}
                  </p>
                </div>
                {order.invoiceInfo.type === 'individual' ? (
                  <>
                    <div>
                      <p className="text-slate-400">Ad Soyad</p>
                      <p className="font-semibold text-slate-800 dark:text-slate-200">{order.invoiceInfo.fullName}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">TCKN</p>
                      <p className="font-semibold text-slate-800 dark:text-slate-200 font-mono">{order.invoiceInfo.identityNumber}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-slate-400">Şirket Unvanı</p>
                      <p className="font-semibold text-slate-800 dark:text-slate-200">{order.invoiceInfo.companyName}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Vergi No & Dairesi</p>
                      <p className="font-semibold text-slate-800 dark:text-slate-200 font-mono">
                        {order.invoiceInfo.taxNumber} — {order.invoiceInfo.taxOffice}
                      </p>
                    </div>
                  </>
                )}
                <div className="col-span-2">
                  <p className="text-slate-400">Fatura Adresi</p>
                  <p className="font-medium text-slate-800 dark:text-slate-200">{order.invoiceInfo.billingAddress}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column (5 cols) - Operations & Actions Sidebar */}
        <div className="lg:col-span-5 space-y-6">
          {/* Preparation & Proof Photo Gallery */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 space-y-4 shadow-xs">
            <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                Hazırlık & Teslimat Görseli
              </span>
              {order.orderPreview?.photoUrl && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                  Görsel Yüklendi
                </span>
              )}
            </h2>

            {order.orderPreview?.photoUrl ? (
              <div className="space-y-3">
                <div className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 group">
                  <img
                    src={order.orderPreview.photoUrl}
                    alt="Sipariş Hazırlık Önizlemesi"
                    className="w-full h-48 object-cover group-hover:scale-105 transition duration-300"
                  />
                  <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                    <a
                      href={order.orderPreview.photoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 bg-white rounded-full text-slate-900 shadow-lg"
                      title="Büyüt"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                {/* Timestamps & Customer Approval State */}
                <div className="p-3.5 bg-slate-50 dark:bg-slate-800 rounded-xl space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Görsel Durumu:</span>
                    {order.orderPreview.isApproved ? (
                      <span className="font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Onaylandı {order.orderPreview.autoApproved ? '(Otomatik)' : ''}
                      </span>
                    ) : order.orderPreview.isApproved === false ? (
                      <span className="font-bold text-rose-600 dark:text-rose-400 flex items-center gap-1">
                        <XCircle className="w-4 h-4" /> Müşteri Reddetti
                      </span>
                    ) : (
                      <span className="font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <Clock className="w-4 h-4 animate-spin" /> Onay Bekleniyor (20 dk)
                      </span>
                    )}
                  </div>

                  {order.orderPreview.uploadedAt && formatOrderDate(order.orderPreview.uploadedAt) && (
                    <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 pt-1 border-t border-slate-200/60 dark:border-slate-700/60">
                      <span>📸 Yükleme Zamanı:</span>
                      <span className="font-medium font-mono">{formatOrderDate(order.orderPreview.uploadedAt)}</span>
                    </div>
                  )}

                  {order.orderPreview.approvedAt && formatOrderDate(order.orderPreview.approvedAt) && (
                    <div className="flex items-center justify-between text-[11px] text-emerald-600 dark:text-emerald-400 pt-1 border-t border-slate-200/60 dark:border-slate-700/60">
                      <span>✅ Onay Zamanı:</span>
                      <span className="font-medium font-mono">{formatOrderDate(order.orderPreview.approvedAt)}</span>
                    </div>
                  )}
                </div>

                {/* Photo Actions */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {!order.orderPreview.isApproved && (
                    <button
                      onClick={async () => {
                        setUpdating(true);
                        try {
                          if (isVendor) {
                            await vendorOrderApi.approveOrderPreview(id!);
                          } else {
                            await orderApi.approveOrderPreview(id!);
                          }
                          await loadOrder();
                          toast.success('Hazırlık görseli onaylandı');
                        } catch (err: any) {
                          toast.error(err?.message || 'Görsel onaylanamadı');
                        } finally {
                          setUpdating(false);
                        }
                      }}
                      disabled={updating}
                      className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition flex items-center justify-center gap-1.5 shadow-xs disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Görseli Manuel Onayla
                    </button>
                  )}
                  <button
                    onClick={handleDeletePreview}
                    disabled={updating}
                    className="px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-xl text-xs font-semibold transition flex-1 flex items-center justify-center gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Sil
                  </button>
                  <label className={`px-3 py-2 bg-slate-900 dark:bg-slate-100 hover:bg-slate-800 text-white dark:text-slate-900 rounded-xl text-xs font-semibold transition flex-1 flex items-center justify-center gap-1.5 cursor-pointer ${uploadingPreview ? 'opacity-50' : ''}`}>
                    <Upload className="w-3.5 h-3.5" />
                    {uploadingPreview ? 'Yükleniyor...' : 'Değiştir'}
                    <input type="file" accept="image/*" onChange={handlePreviewUpload} className="hidden" disabled={uploadingPreview} />
                  </label>
                </div>
              </div>
            ) : (
              <div className="p-6 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl text-center space-y-3">
                <p className="text-xs text-slate-500 dark:text-slate-400">Müşteriye gönderilecek çiçek/hediye hazırlık fotoğrafını yükleyin.</p>
                <label className={`inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold cursor-pointer transition shadow-xs ${uploadingPreview ? 'opacity-50' : ''}`}>
                  <Upload className="w-4 h-4" />
                  {uploadingPreview ? 'Yükleniyor...' : 'Hazırlık Fotoğrafı Yükle'}
                  <input type="file" accept="image/*" onChange={handlePreviewUpload} className="hidden" disabled={uploadingPreview} />
                </label>
              </div>
            )}
          </div>

          {/* Vendor Assignment Panel */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 space-y-4 shadow-xs">
            <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Satıcı Atama
            </h2>

            {order.assignedVendorId ? (
              assignedVendorInfo ? (
                <div className="p-4 bg-indigo-50/60 dark:bg-indigo-950/30 rounded-xl border border-indigo-200 dark:border-indigo-900/40 space-y-2">
                  <p className="text-[11px] font-bold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider">Atanan Satıcı</p>
                  <p className="text-base font-bold text-slate-900 dark:text-white">
                    {assignedVendorInfo.firstName} {assignedVendorInfo.lastName}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-300 pt-2 border-t border-indigo-200 dark:border-indigo-900/40">
                    <div>
                      <span className="text-slate-400 block text-[10px]">Telefon</span>
                      <span className="font-mono font-medium">{assignedVendorInfo.phone}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">E-posta</span>
                      <span className="truncate block">{assignedVendorInfo.email}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-400">Satıcı bilgisi yükleniyor...</p>
              )
            ) : order.status === 'delivered' ? (
              <p className="text-xs text-rose-600 font-medium">⛔ Teslim edilen siparişe satıcı atanamaz.</p>
            ) : (
              <div className="space-y-3">
                <select
                  value={selectedVendorId}
                  onChange={e => setSelectedVendorId(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-xl px-3 py-2 text-xs focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Satıcı seçiniz...</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.firstName} {v.lastName} ({v.email})
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAssignVendor}
                  disabled={assigningVendor || !selectedVendorId}
                  className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold transition disabled:opacity-50"
                >
                  {assigningVendor ? 'Atanıyor...' : 'Satıcıyı Ata'}
                </button>
              </div>
            )}
          </div>

          {/* Kargo Takip Bilgisi Panel */}
          {(order.items.some(item => item.isShippable) || order.shipmentTrackingUrl) && (
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 space-y-4 shadow-xs">
              <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  Kargo Takip Bilgisi
                </span>
                {order.shipmentTrackingUrl && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                    Takip Ekli
                  </span>
                )}
              </h2>

              {isAdmin ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">
                      Kargo Takip Bağlantısı (URL)
                    </label>
                    <input
                      type="url"
                      value={shipmentTrackingUrl}
                      onChange={(e) => setShipmentTrackingUrl(e.target.value)}
                      placeholder="https://kargo-firmasi.com/takip/..."
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-xl px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">
                      Boş bırakıp kaydederseniz mevcut takip linki kaldırılır.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleShipmentTrackingSave}
                      disabled={savingTrackingUrl}
                      className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition flex items-center justify-center gap-1.5 shadow-xs disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {savingTrackingUrl ? 'Kaydediliyor...' : 'Takip Linkini Kaydet'}
                    </button>
                    {order.shipmentTrackingUrl && (
                      <a
                        href={order.shipmentTrackingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3.5 py-2.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-blue-600 dark:text-blue-400 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-1.5 hover:bg-slate-200"
                        title="Linki Aç"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Aç
                      </a>
                    )}
                  </div>
                </div>
              ) : order.shipmentTrackingUrl ? (
                <a
                  href={order.shipmentTrackingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-2.5 bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-2 hover:bg-blue-100"
                >
                  <ExternalLink className="w-4 h-4" />
                  Kargo Takip Linkini Aç
                </a>
              ) : (
                <p className="text-xs text-slate-400">Kargo takip linki henüz eklenmemiş.</p>
              )}
            </div>
          )}

          {/* Courier Assignment Panel */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 space-y-4 shadow-xs">
            <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Truck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              Kurye Atama & Teslimat
            </h2>

            {order.assignedCourierId ? (
              assignedCourierInfo ? (
                <div className="p-4 bg-emerald-50/60 dark:bg-emerald-950/30 rounded-xl border border-emerald-200 dark:border-emerald-900/40 space-y-2">
                  <p className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Atanan Kurye</p>
                  <p className="text-base font-bold text-slate-900 dark:text-white">
                    {assignedCourierInfo.firstName} {assignedCourierInfo.lastName}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-300 pt-2 border-t border-emerald-200 dark:border-emerald-900/40">
                    <div>
                      <span className="text-slate-400 block text-[10px]">Telefon</span>
                      <span className="font-mono font-medium">{assignedCourierInfo.phone}</span>
                    </div>
                    {order.courierFee && (
                      <div>
                        <span className="text-slate-400 block text-[10px]">Kurye Ücreti</span>
                        <span className="font-mono font-bold text-emerald-700 dark:text-emerald-300">₺{order.courierFee.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {order.deliveryProof?.photoUrl && (
                    <div className="pt-2 border-t border-emerald-200 dark:border-emerald-900/40 space-y-1">
                      <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 block">Kurye Teslim Kanıtı Fotoğrafı</span>
                      <a href={order.deliveryProof.photoUrl} target="_blank" rel="noreferrer">
                        <img src={order.deliveryProof.photoUrl} alt="Teslim Kanıtı" className="w-full h-28 object-cover rounded-lg border border-emerald-200 dark:border-emerald-800" />
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-400">Kurye bilgisi yükleniyor...</p>
              )
            ) : order.status === 'awaiting_bank_transfer' ? (
              <p className="text-xs text-amber-600 bg-amber-50 p-2.5 rounded-xl border border-amber-200">
                🔒 Havale onaylanmadan kurye atanamaz.
              </p>
            ) : (
              <div className="space-y-3">
                <select
                  value={selectedCourierId}
                  onChange={e => setSelectedCourierId(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-xl px-3 py-2 text-xs focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Kurye seçiniz...</option>
                  {availableCouriers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.firstName} {c.lastName} ({c.phone})
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="Kurye taşıma ücreti (₺ opsiyonel)"
                  value={courierFee}
                  onChange={e => setCourierFee(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-xl px-3 py-2 text-xs"
                />
                <button
                  onClick={handleAssignCourier}
                  disabled={assigningCourier || !selectedCourierId}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition disabled:opacity-50"
                >
                  {assigningCourier ? 'Atanıyor...' : 'Kuryeyi Ata'}
                </button>
              </div>
            )}
          </div>

          {/* E-Invoice Operations Panel */}
          {isAdmin && (
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 space-y-4 shadow-xs">
              <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  E-Fatura İşlemleri
                </span>
                <span className={`text-xs px-2.5 py-0.5 rounded-full font-bold ${INVOICE_STATUS_COLORS[order.invoiceLifecycle?.status || 'none']}`}>
                  {INVOICE_STATUS_LABELS[order.invoiceLifecycle?.status || 'none']}
                </span>
              </h2>

              <div className="space-y-2.5 text-xs">
                {order.invoiceLifecycle?.invoiceNumber && (
                  <div className="flex justify-between py-1 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-slate-400">Fatura No:</span>
                    <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">{order.invoiceLifecycle.invoiceNumber}</span>
                  </div>
                )}

                {order.invoiceLifecycle?.invoicedAt && (
                  <div className="flex justify-between py-1 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-slate-400">Kesim Tarihi:</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200">{new Date(order.invoiceLifecycle.invoicedAt).toLocaleString('tr-TR')}</span>
                  </div>
                )}

                {order.invoiceLifecycle?.lastError && (
                  <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 rounded-xl text-rose-700 dark:text-rose-300">
                    ⚠️ {order.invoiceLifecycle.lastError}
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-1">
                {order.invoiceInfo && (
                  <button
                    onClick={() => setShowInvoicePreview(true)}
                    className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Fatura Detayı & Önizleme
                  </button>
                )}

                {order.invoiceLifecycle?.invoiceUrl && (
                  <a
                    href={order.invoiceLifecycle.invoiceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full py-2.5 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-2 hover:bg-emerald-100"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Resmi Belgeyi Aç
                  </a>
                )}

                <button
                  onClick={handleIssueInvoice}
                  disabled={issuingInvoice || !order.invoiceInfo || order.status !== 'delivered' || order.invoiceLifecycle?.status === 'processing' || order.invoiceLifecycle?.status === 'invoiced'}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-xs"
                >
                  {issuingInvoice ? 'Fatura Kesiliyor...' : order.invoiceLifecycle?.status === 'invoiced' ? 'Fatura Kesildi ✓' : 'Faturayı Kes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invoice Preview Modal */}
      <Modal
        isOpen={showInvoicePreview}
        title="Fatura Ön İzleme"
        onClose={() => setShowInvoicePreview(false)}
        size="xl"
        actions={
          <button onClick={() => setShowInvoicePreview(false)} className="btn-primary">
            Kapat
          </button>
        }
      >
        {order && order.invoiceInfo ? (
          <div className="space-y-6 text-sm text-slate-700 dark:text-slate-200">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50/50 dark:bg-slate-800/40">
                <h3 className="mb-3 text-base font-bold text-slate-900 dark:text-white">Belge Özeti</h3>
                <div className="space-y-2 text-xs">
                  <p><strong>Sipariş No:</strong> #{order.orderNo}</p>
                  <p><strong>Belge Tipi:</strong> {order.invoiceLifecycle?.documentType === 'e_invoice' ? 'e-Fatura' : 'e-Arşiv'}</p>
                  <p><strong>Durum:</strong> {INVOICE_STATUS_LABELS[order.invoiceLifecycle?.status || 'none']}</p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50/50 dark:bg-slate-800/40">
                <h3 className="mb-3 text-base font-bold text-slate-900 dark:text-white">Fatura Alıcısı</h3>
                <div className="space-y-2 text-xs">
                  <p><strong>Tip:</strong> {order.invoiceInfo.type === 'individual' ? 'Bireysel' : 'Kurumsal'}</p>
                  {order.invoiceInfo.type === 'individual' ? (
                    <>
                      <p><strong>Ad Soyad:</strong> {order.invoiceInfo.fullName}</p>
                      <p><strong>TCKN:</strong> {order.invoiceInfo.identityNumber}</p>
                    </>
                  ) : (
                    <>
                      <p><strong>Şirket:</strong> {order.invoiceInfo.companyName}</p>
                      <p><strong>Vergi No:</strong> {order.invoiceInfo.taxNumber} ({order.invoiceInfo.taxOffice})</p>
                    </>
                  )}
                  <p><strong>Adres:</strong> {order.invoiceInfo.billingAddress}</p>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-xs">
                <thead className="bg-slate-100 dark:bg-slate-800 font-semibold text-slate-600 dark:text-slate-300">
                  <tr>
                    <th className="px-4 py-3 text-left">Kalem</th>
                    <th className="px-4 py-3 text-center">Miktar</th>
                    <th className="px-4 py-3 text-right">Birim Fiyat</th>
                    <th className="px-4 py-3 text-right">Toplam</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {invoicePreviewRows.map((row, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3 font-medium">{row.name}</td>
                      <td className="px-4 py-3 text-center">{row.quantity}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(row.unitPrice)}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">{formatCurrency(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ml-auto max-w-md space-y-2 rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50/50 dark:bg-slate-800/40 text-xs">
              <div className="flex justify-between"><span>Ara Toplam</span><span className="font-mono">{formatCurrency(invoicePreviewSubTotal)}</span></div>
              <div className="flex justify-between"><span>İskonto</span><span className="font-mono">{formatCurrency(invoicePreviewDiscount)}</span></div>
              <div className="flex justify-between"><span>KDV Hariç</span><span className="font-mono">{formatCurrency(invoicePreviewNet)}</span></div>
              <div className="flex justify-between"><span>KDV (%20)</span><span className="font-mono">{formatCurrency(invoicePreviewTax)}</span></div>
              <div className="flex justify-between border-t border-slate-300 dark:border-slate-700 pt-2 text-sm font-bold text-slate-900 dark:text-white">
                <span>Genel Toplam</span>
                <span className="font-mono text-emerald-600 dark:text-emerald-400">{formatCurrency(order.totalPrice)}</span>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Cancel Order Modal */}
      <Modal
        isOpen={showCancelModal}
        title="Siparişi İptal Et"
        onClose={() => setShowCancelModal(false)}
        actions={
          <>
            <button onClick={() => setShowCancelModal(false)} className="btn-secondary" disabled={cancelling}>
              Vazgeç
            </button>
            <button onClick={handleConfirmCancel} className="btn-primary bg-rose-600 hover:bg-rose-700 font-semibold" disabled={cancelling}>
              {cancelling ? 'İptal Ediliyor...' : 'Evet, Siparişi İptal Et'}
            </button>
          </>
        }
      >
        <div className="space-y-3 text-xs">
          <p className="text-slate-600 dark:text-slate-300">
            Bu sipariş neden iptal ediliyor? (ör: stokta yok, müşteri talebi, adres hatalı)
          </p>
          <textarea
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            rows={3}
            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-xl p-3 text-xs focus:ring-2 focus:ring-rose-500"
            placeholder="İptal nedeni..."
            autoFocus
          />
          {order.paymentInfo.method === 'credit-card' && order.paymentInfo.isProcessed && (
            <p className="text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 p-2.5 rounded-xl border border-amber-200 dark:border-amber-900/40">
              ⚠️ Bu siparişin ödemesi kredi kartıyla alınmış. Siparişi iptal ettikten sonra gerekiyorsa "Ödeme Bilgisi" kartından ayrıca krediye iade yapabilirsiniz.
            </p>
          )}
        </div>
      </Modal>

      {/* Refund Credit Card Modal */}
      <Modal
        isOpen={showRefundModal}
        title="Krediye İade / İptal İşlemi"
        onClose={() => setShowRefundModal(false)}
        actions={
          <>
            <button onClick={() => setShowRefundModal(false)} className="btn-secondary" disabled={refunding}>
              Vazgeç
            </button>
            <button onClick={handleConfirmRefund} className="btn-primary bg-rose-600 hover:bg-rose-700 font-semibold" disabled={refunding}>
              {refunding ? 'Gönderiliyor...' : 'İade İşlemini Onayla'}
            </button>
          </>
        }
      >
        <div className="space-y-3 text-xs">
          <div>
            <label className="text-slate-600 dark:text-slate-400 block mb-1">Tutar (₺)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={refundAmount}
              onChange={e => setRefundAmount(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-xl p-2.5 text-xs"
            />
          </div>
          <div>
            <label className="text-slate-600 dark:text-slate-400 block mb-1">İşlem Tipi</label>
            <select
              value={refundType}
              onChange={e => setRefundType(e.target.value as 'cancel' | 'refund')}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-xl p-2.5 text-xs"
            >
              <option value="cancel">İptal — ödeme bugün alındıysa</option>
              <option value="refund">İade — ödeme önceki günlerde alındıysa</option>
            </select>
          </div>
          <div>
            <label className="text-slate-600 dark:text-slate-400 block mb-1">Neden</label>
            <textarea
              value={refundReason}
              onChange={e => setRefundReason(e.target.value)}
              rows={2}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-xl p-2.5 text-xs"
              placeholder="ör: Ürün temin edilemedi"
            />
          </div>
        </div>
      </Modal>

      {/* Kart Notu Yazdırma ve Şablon Modalı */}
      <Modal
        isOpen={showPrintCardModal}
        title="🖨️ Kart Notu Yazdırma Ayarları"
        onClose={() => setShowPrintCardModal(false)}
        size="xl"
        actions={
          <div className="flex justify-between items-center w-full">
            <button onClick={() => setShowPrintCardModal(false)} className="btn-secondary">
              Vazgeç
            </button>
            <button
              onClick={handlePrintCard}
              className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-xs transition flex items-center gap-2 shadow-xs"
            >
              <Printer className="w-4 h-4" />
              Yazıcıya Gönder (Print)
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-xs text-slate-700 dark:text-slate-200">
          {/* Controls - Left (6 cols) */}
          <div className="lg:col-span-6 space-y-4">
            {/* Presets */}
            <div>
              <label className="font-bold text-slate-900 dark:text-white block mb-1.5">1. Matbu Kart Ölçüsü (Şablon Seçin)</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handlePresetChange('10x15')}
                  className={`p-2.5 rounded-xl border text-center font-medium transition ${
                    cardPreset === '10x15'
                      ? 'bg-purple-600 text-white border-purple-600 font-bold'
                      : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  10 x 15 cm (Standart Çiçek Kartı)
                </button>
                <button
                  onClick={() => handlePresetChange('8x12')}
                  className={`p-2.5 rounded-xl border text-center font-medium transition ${
                    cardPreset === '8x12'
                      ? 'bg-purple-600 text-white border-purple-600 font-bold'
                      : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  8 x 12 cm (Küçük Kart)
                </button>
                <button
                  onClick={() => handlePresetChange('80mm')}
                  className={`p-2.5 rounded-xl border text-center font-medium transition ${
                    cardPreset === '80mm'
                      ? 'bg-purple-600 text-white border-purple-600 font-bold'
                      : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  80 mm (Termal Rulo)
                </button>
                <button
                  onClick={() => setCardPreset('custom')}
                  className={`p-2.5 rounded-xl border text-center font-medium transition ${
                    cardPreset === 'custom'
                      ? 'bg-purple-600 text-white border-purple-600 font-bold'
                      : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  Özel Ölçü
                </button>
              </div>
            </div>

            {/* Custom Dimensions */}
            <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-700">
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Genişlik (mm)</label>
                <input
                  type="number"
                  value={cardWidth}
                  onChange={e => { setCardWidth(Number(e.target.value)); setCardPreset('custom'); }}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-2 font-mono"
                />
              </div>
              <div>
                <label className="block text-slate-500 dark:text-slate-400 mb-1">Yükseklik (mm)</label>
                <input
                  type="number"
                  value={cardHeight}
                  onChange={e => { setCardHeight(Number(e.target.value)); setCardPreset('custom'); }}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-2 font-mono"
                />
              </div>
            </div>

            {/* Margins */}
            <div>
              <label className="font-bold text-slate-900 dark:text-white block mb-1.5">2. Baskı Konumlandırma Boşlukları (Margin)</label>
              <div className="space-y-3 p-3 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-700">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-slate-600 dark:text-slate-300">Üst Boşluk (Logolu matbu alan için):</span>
                    <span className="font-bold font-mono">{topMargin} mm</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="80"
                    value={topMargin}
                    onChange={e => setTopMargin(Number(e.target.value))}
                    className="w-full accent-purple-600 cursor-pointer"
                  />
                </div>

                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-slate-600 dark:text-slate-300">Yan Boşluklar (Sol/Sağ):</span>
                    <span className="font-bold font-mono">{leftMargin} mm</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="40"
                    value={leftMargin}
                    onChange={e => setLeftMargin(Number(e.target.value))}
                    className="w-full accent-purple-600 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Typography */}
            <div>
              <label className="font-bold text-slate-900 dark:text-white block mb-1.5">3. Yazı Tipi & Boyutu</label>
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-700">
                <div>
                  <label className="block text-slate-500 mb-1">Yazı Tipi Stil</label>
                  <select
                    value={fontFamily}
                    onChange={e => setFontFamily(e.target.value as any)}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-2"
                  >
                    <option value="cursive">El Yazısı (Script)</option>
                    <option value="serif">Klasik (Georgia Serif)</option>
                    <option value="sans">Modern (Sans-Serif)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-500 mb-1">Punto (Font Size)</label>
                  <input
                    type="number"
                    value={fontSize}
                    onChange={e => setFontSize(Number(e.target.value))}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-2 font-mono"
                  />
                </div>
                <div className="col-span-2 flex gap-2 pt-1">
                  <span className="text-slate-500 text-[11px] self-center mr-2">Hizalama:</span>
                  {(['left', 'center', 'right'] as const).map(align => (
                    <button
                      key={align}
                      onClick={() => setTextAlign(align)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-semibold capitalize flex-1 ${
                        textAlign === align ? 'bg-purple-600 text-white border-purple-600' : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {align === 'left' ? 'Sol' : align === 'center' ? 'Ortala' : 'Sağ'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Options */}
            <div>
              <label className="font-bold text-slate-900 dark:text-white block mb-1.5">4. İçerik Seçenekleri</label>
              <div className="space-y-2 p-3 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-700">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeRecipient}
                    onChange={e => setIncludeRecipient(e.target.checked)}
                    className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span>Alıcı Başlığı Yazılsın ("Sevgili {order?.recipientInfo?.fullName}")</span>
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeSender}
                    disabled={order?.anonymousSender}
                    onChange={e => setIncludeSender(e.target.checked)}
                    className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span>
                    Gönderen İsmi Yazılsın ("— {order?.senderInfo?.name}")
                    {order?.anonymousSender && <span className="text-rose-500 font-bold ml-1">(Anonim Gönderici)</span>}
                  </span>
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeOrderNo}
                    onChange={e => setIncludeOrderNo(e.target.checked)}
                    className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span>Alt Bilgiye Sipariş No Eklensin (Sipariş #{order?.orderNo || order?.id})</span>
                </label>
              </div>
            </div>
          </div>

          {/* Live Card Preview - Right (6 cols) */}
          <div className="lg:col-span-6 flex flex-col items-center justify-start bg-slate-100 dark:bg-slate-800/60 p-5 rounded-2xl border border-slate-200 dark:border-slate-700">
            <h4 className="font-bold text-slate-800 dark:text-slate-200 text-xs mb-3 self-start">
              🖼️ Canlı Kart Baskı Önizlemesi ({cardWidth}mm x {cardHeight}mm)
            </h4>

            {/* Card Simulation */}
            <div
              className="bg-white text-slate-900 border-2 border-dashed border-slate-300 shadow-xl rounded-lg relative overflow-hidden transition-all duration-300"
              style={{
                width: `${Math.min(280, cardWidth * 2.4)}px`,
                height: `${Math.min(400, cardHeight * 2.4)}px`,
                fontFamily: fontFamily === 'cursive' ? "'Dancing Script', cursive" : fontFamily === 'serif' ? 'Georgia, serif' : 'sans-serif',
                fontSize: `${fontSize * 0.9}px`,
                textAlign: textAlign,
                paddingTop: `${topMargin * 1.8}px`,
                paddingLeft: `${leftMargin * 1.8}px`,
                paddingRight: `${leftMargin * 1.8}px`,
              }}
            >
              {topMargin > 0 && (
                <div
                  className="absolute top-0 left-0 right-0 bg-slate-100/80 border-b border-slate-300 text-slate-400 text-[10px] flex items-center justify-center font-sans uppercase tracking-wider"
                  style={{ height: `${topMargin * 1.8}px` }}
                >
                  [ Logolu Alan: {topMargin}mm ]
                </div>
              )}

              {includeRecipient && order?.recipientInfo?.fullName && (
                <div className="font-bold mb-2 text-slate-800">
                  Sevgili {order.recipientInfo.fullName},
                </div>
              )}

              <div className="whitespace-pre-wrap leading-relaxed">
                {order?.cardNote || 'Kart notu içeriği...'}
              </div>

              {includeSender && order?.senderInfo?.name && !order?.anonymousSender && (
                <div className="font-bold mt-3 text-slate-800">
                  — {order.senderInfo.name}
                </div>
              )}

              {includeOrderNo && (
                <div className="absolute bottom-2 right-2 text-[9px] text-slate-400 font-sans">
                  Sipariş #{order?.orderNo || order?.id}
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-3 text-center">
              Gri desenli üst alan, fiziksel matbu kartınızın üzerindeki logolu boşluğu temsil eder.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};


/**
 * Sipariş kalemi satırında, modale girmeden doğrudan ürünün maliyet fiyatını (costPrice)
 * görüp güncellemeyi sağlar. Bu değer üründe global olarak saklanır (muhasebe hesaplamalarında kullanılır).
 */
const ProductCostInlineEditor: React.FC<{ productId: string }> = ({ productId }) => {
  const [costPrice, setCostPrice] = useState('');
  const [originalCost, setOriginalCost] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const p = await productApi.getProduct(productId);
        if (cancelled) return;
        const cp = typeof p.costPrice === 'number' ? p.costPrice : 0;
        setOriginalCost(cp);
        setCostPrice(String(cp));
      } catch {
        // sessiz geç — satır içi editör, hata olursa boş kalır
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  const handleSave = async () => {
    const value = Number(costPrice);
    if (Number.isNaN(value) || value < 0) {
      toast.error('Geçerli bir maliyet girin');
      return;
    }
    setSaving(true);
    try {
      await productApi.updateProduct(productId, { costPrice: value } as any);
      setOriginalCost(value);
      toast.success('Maliyet güncellendi');
    } catch (e: any) {
      toast.error(e.message || 'Maliyet güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const changed = originalCost !== null && Number(costPrice) !== originalCost;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2"
    >
      <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">Maliyet:</span>
      {loading ? (
        <span className="text-xs text-gray-500 dark:text-gray-400">Yükleniyor...</span>
      ) : (
        <>
          <span className="text-sm text-gray-600 dark:text-gray-300">₺</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={costPrice}
            onChange={(e) => setCostPrice(e.target.value)}
            disabled={saving}
            className="w-24 rounded border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
          />
          <button
            onClick={handleSave}
            disabled={saving || !changed}
            className="rounded bg-amber-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-amber-700 disabled:opacity-50"
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </>
      )}
    </div>
  );
};
